<script lang="ts" setup>
import {
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
} from "tir-style-system/icons/outline";
import { TirPmInput } from "tir-pm-input";

import { computed, onMounted, ref, watch } from "vue";
import { TirPmSpinner } from "tir-pm-spinner";
import ApiService from "@/shared/api";
import { useRoute, useRouter } from "vue-router";
import { CurrentUser, CurrentUserModel, UserRoleEnum } from "tir-pm-user-core";
import TypeRoles from "@/shared/api/Main/dataSource/api/users/implementation/TypeRoles";

const router = useRouter();

// Глобальный сервис
const api = new ApiService();

let route = useRoute();

/**
 * Забрать токен с урла
 */
let routeQueryToken = ref(route.params.token);
if (routeQueryToken.value[routeQueryToken.value.length - 1] == "/") {
  routeQueryToken.value = routeQueryToken.value.slice(0, -1);
}

// Переменные для паролей
const firstPassword = ref("");
const secondPassword = ref("");

// Переменные для показа или скрытия паролей
const showFirstPassword = ref(false);
const showSecondPassword = ref(false);

// Переменная чтобы показать компонент ошибки
const showPlateWarning = ref(false);

// Переменная в которую заходит текст ошибки
const errorMessage = ref("");

/**
 * Блок элемента контента
 */
const $currentComponentWrapper = ref(null);

/**
 * Текущая высота элемента контента
 */
const currentHeightComponent = ref(null);

/**
 * Индикатор загрузки юзера
 */
const isLoadingCurrentUser = ref(true);

/**
 * Индикатор загрузки юзера
 */
const isSuccessEqualsPasword = ref(false);

/**
 * Текущий юзер
 **/
const currentUserNameFromToken = ref("");

/**
 * Лоадер для кнопки
 */
const loadingRequest = ref(false);

/**
 * Перерасчет высоты
 */
const loadingRequestComputed = computed(() => {
  return loadingRequest.value;
});

/**
 *  Переключение типов паролей для инпутов, чтобы показать введенное
 */
const typeFirstPassword = computed(() => {
  return showFirstPassword.value ? "text" : "password";
});
const typeSecondPassword = computed(() => {
  return showSecondPassword.value ? "text" : "password";
});

/**
 * Функция валидации
 */
const checkValidate = computed(() => {
  return (
    firstPassword.value.length &&
    secondPassword.value.length &&
    firstPassword.value == secondPassword.value
  );
});

const mapRole = (role: TypeRoles) => {
  return {
    [TypeRoles.Employee]: UserRoleEnum.Employee,
    [TypeRoles.Owner]: UserRoleEnum.Owner,
    [TypeRoles.NamespaceAdmin]: UserRoleEnum.NamespaceAdmin,
    [TypeRoles.ProjectAdmin]: UserRoleEnum.ProjectAdmin,
  }[role];
};

/**
 * Функция для добавления юзера и редиректа при успешном заполнении
 */
const onInviteUser = async () => {
  loadingRequest.value = true;
  if (isSuccessEqualsPasword.value) {
    try {
      await api.Main.UsersService.activateAsync({
        Token: routeQueryToken.value,
        Password: secondPassword.value,
      });
      const userResp = await api.Main.UsersService.getCurrentUserAsync();
      CurrentUser.set(
        new CurrentUserModel({
          Id: userResp.Id,
          Email: userResp.Email,
          FirstName: userResp.FirstName,
          LastName: userResp.LastName,
          MiddleName: userResp.MiddleName,
          AvatarColor: userResp.AvatarColor,
          Foto: userResp.Foto,
          Roles: userResp.Roles.map(mapRole),
        })
      );
      showPlateWarning.value = false;
      router.push("/projects");
    } catch (err: any) {
      console.error(err);
      isSuccessEqualsPasword.value = false;
      showPlateWarning.value = true;
      errorMessage.value = err?.ErrorMessage;
    }
  } else {
    isSuccessEqualsPasword.value = false;
    showPlateWarning.value = true;
    errorMessage.value = "Пароли не совпадают";
  }
  loadingRequest.value = false;
  updateHeight();
};

/**
 * Функция активируемая набором в инпут
 */
const onTypeOfInput = () => {
  showPlateWarning.value = false;
  errorMessage.value = "";
};

/**
 * Функция обновления высоты
 */
const updateHeight = () => {
  setTimeout(() => {
    currentHeightComponent.value = $currentComponentWrapper.value?.offsetHeight;
  });
};

/**
 * Получить имя
 */
onMounted(async () => {
  currentHeightComponent.value = $currentComponentWrapper.value?.offsetHeight;
  try {
    let { FirstName, LastName, MiddleName } =
      await api.Main.UsersService.getUserShortInfoByTokenActivationAsync({
        Token: routeQueryToken.value,
      });

    currentUserNameFromToken.value = `${LastName} ${FirstName} ${MiddleName}`;
    isLoadingCurrentUser.value = false;
  } catch (error) {
    router.push("/login");
  }
});

watch(checkValidate, () => {
  if (checkValidate.value) {
    isSuccessEqualsPasword.value = true;
  } else {
    isSuccessEqualsPasword.value = false;
  }
});

defineExpose({
  changerMethodFromComponent: onInviteUser,
  currentHeightComponent: currentHeightComponent,
  isLoadingIntoComponent: loadingRequestComputed,
});
</script>

<template>
  <div class="layout-login__body" ref="$currentComponentWrapper">
    <img class="layout-login__logo" src="../../assets/images/Logo.svg" alt="" />
    <div class="layout-login__inner-title-invite">
      <TirPmSpinner v-if="isLoadingCurrentUser"></TirPmSpinner>
      <div class="layout-login__invite-title">Добро пожаловать,</div>
      <div class="layout-login__invite-subtitle">
        {{ currentUserNameFromToken }}
      </div>
    </div>
    <div class="layout-login__wrap-input">
      <TirPmInput
        isAutocomplete
        label="Пароль"
        :type="typeFirstPassword"
        v-model="firstPassword"
        placeholder=""
        :isWithSuccess="isSuccessEqualsPasword"
        @input="onTypeOfInput"
        :isWithHint="false"
      >
        <template #leftInputAddons>
          <div class="layout-login__input-addons">
            <KeyIcon></KeyIcon>
          </div>
        </template>
        <template #hintMessage></template>
        <template #rightInputAddons>
          <div
            class="layout-login__input-addons layout-login__input-addons--action"
            :class="{
              'layout-login__input-addons--show': firstPassword.length,
            }"
          >
            <EyeIcon
              @click="showFirstPassword = !showFirstPassword"
              v-if="!showFirstPassword"
            ></EyeIcon>
            <EyeSlashIcon
              @click="showFirstPassword = !showFirstPassword"
              v-if="showFirstPassword"
            ></EyeSlashIcon>
          </div>
        </template>
      </TirPmInput>
      <TirPmInput
        isAutocomplete
        label="Повторите пароль"
        :type="typeSecondPassword"
        v-model="secondPassword"
        placeholder=""
        :isWithHint="showPlateWarning"
        :is-show-hint="showPlateWarning"
        @input="onTypeOfInput"
        :isWithSuccess="isSuccessEqualsPasword"
        :isWithError="showPlateWarning"
      >
        <template #leftInputAddons>
          <div class="layout-login__input-addons">
            <KeyIcon></KeyIcon>
          </div>
        </template>
        <template #hintMessage> {{ errorMessage }} </template>
        <template #rightInputAddons>
          <div
            class="layout-login__input-addons layout-login__input-addons--action"
            :class="{
              'layout-login__input-addons--show': secondPassword.length,
            }"
          >
            <EyeIcon
              @click="showSecondPassword = !showSecondPassword"
              v-if="!showSecondPassword"
            ></EyeIcon>
            <EyeSlashIcon
              @click="showSecondPassword = !showSecondPassword"
              v-if="showSecondPassword"
            >
            </EyeSlashIcon>
          </div>
        </template>
      </TirPmInput>
    </div>
  </div>
</template>
